<template>
    <div class="drag-pipeline-row" :id="item._id" :class="[selected && 'selected']">
        <div v-if="item.attachments.length > 0" id="image-preview" @click="view">
            <img
                :src="`https://static.supsis.live/${item.attachments[0].path + item.attachments[0]._id}.${
                    item.attachments[0].ext
                }`"
                alt="attachment"
            />
        </div>
        <div class="drag-pipeline-row-card" :type="pipeline.type">
            <div class="row-check">
                <a-checkbox class="kanban-checkbox" v-model="selected" @change="(e) => check(item, e)" />
            </div>
            <div class="content" @click="view">
                <div class="task-head">
                    <div class="task-title">
                        {{ item.title }}
                    </div>
                    <a-popover
                        @click.stop="handleInnerClick"
                        overlayClassName="task-more-options"
                        trigger="click"
                        placement="bottomRight"
                        class="kanban-task-edit-popover"
                        v-model:visible="showPopover"
                    >
                        <div slot="content">
                            <template v-for="(action, index) of actions" v-if="isShowingAction(action)">
                                <div class="bracket" v-if="index !== 0"></div>
                                <div class="options-item" @click="runAction(action.action, item)">
                                    <div class="material-icons">{{ action.icon }}</div>
                                    {{ action.text }}
                                </div>
                            </template>
                        </div>
                        <div class="task-more material-icons">more_vert</div>
                    </a-popover>
                </div>
                <div class="kanban-task-details" @click="view">
                    <div class="task-users">
                        <Avatar
                            v-for="userId of getUserIds(item)"
                            :size="25"
                            :key="userId"
                            :src="users[userId] && users[userId].gravatar"
                            :username="users[userId] && users[userId].name"
                            v-tippy="{
                                followCursor: true,
                                placement: 'bottom-end',
                            }"
                            :content="users[userId] && users[userId].email"
                        />
                        <div>
                            <LowIcon v-if="item.priority === 'low'" />
                            <LowestIcon v-else-if="item.priority === 'lowest'" />
                            <MediumIcon v-else-if="item.priority === 'medium'" />
                            <HighIcon v-else-if="item.priority === 'high'" />
                            <HighestIcon v-else-if="item.priority === 'highest'" />
                        </div>
                    </div>
                    <div class="kanban-task-alt-items">
                        <div class="kanban-task-alt-items-item" v-if="(item.subTasks || []).length > 0">
                            <div class="material-icons">chat</div>
                        </div>
                        <div class="kanban-task-alt-items-item" v-if="(item.subTasks || []).length > 0">
                            <div class="material-icons">account_tree</div>
                            {{ getCompletedParentTaskCount(item) }}/{{ (item.subTasks || []).length }}
                        </div>
                        <div class="kanban-task-alt-items-item" v-if="item.checklist.length > 0">
                            <div class="material-icons">library_add_check</div>
                            {{ getCompletedChecklistCount(item) }}/{{ item.checklist.length }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import Avatar from "vue-avatar";
import { mapActions, mapGetters, mapState } from "vuex";
import _ from "lodash";
import LowIcon from "../../../../components/svg/task/LowIcon.vue";
import LowestIcon from "../../../../components/svg/task/LowestIcon.vue";
import MediumIcon from "../../../../components/svg/task/MediumIcon.vue";
import HighIcon from "../../../../components/svg/task/HighIcon.vue";
import HighestIcon from "../../../../components/svg/task/HighestIcon.vue";
import { PERMISSION_TYPES } from "../../../../language/permissionTypes";

export default {
    name: "TaskCard",
    props: {
        item: {
            type: Object,
            default: {},
        },
        pipeline: {
            type: Object,
            default: {},
        },
        workflowId: {
            type: String,
            default: null,
        },
    },
    components: {
        Avatar,
        LowIcon,
        LowestIcon,
        MediumIcon,
        HighIcon,
        HighestIcon,
    },
    data: () => ({
        showPopover: false,
        selected: false,

        relations: {},
    }),
    computed: {
        ...mapState({
            user: (state) => state.auth.user,
            users: (state) =>
                state.user.users.data.reduce((acc, user) => {
                    acc[user._id] = user;
                    return acc;
                }, {}),
            checkedItems: (state) => state.tasks.checkedTasks,
            workflows: (state) =>
                state.tasks.workflows.reduce((acc, workflow) => {
                    acc[workflow._id] = workflow;
                    return acc;
                }, {}),
        }),
        ...mapGetters({
            checkPermission: "auth/checkPermission",
        }),
        actions() {
            return [
                {
                    icon: "edit",
                    text: this.$t("settings.edit"),
                    action: "edit",
                },
                {
                    icon: "delete",
                    text: this.$t("chatbotv2.delete"),
                    action: "deleteTaskModalOpen",
                },
                {
                    type: "goto_message",
                    icon: "forward",
                    text: "Go to Message",
                    action: "gotoMessage",
                },

                {
                    type: "goto_chat",
                    icon: "forward",
                    text: "Go to Chat",
                    action: "gotoChat",
                },

                {
                    type: "goto_visitor",
                    icon: "forward",
                    text: "Show Visitor",
                    action: "showVisitor",
                },
            ];
        },
    },
    methods: {
        ...mapActions({
            addNewWindow: "app/addNewWindow",
            setCheckedTasks: "tasks/setCheckedTasks",
        }),
        getUserIds(item) {
            return _.uniq([item.assignees, item.collaborators, item.followers, item.owner].flat()).filter(
                (c) => this.users[c]
            );
        },
        getCompletedParentTaskCount(item) {
            return (item.subTasks || []).filter((item) => item.completed).length;
        },
        getCompletedChecklistCount(item) {
            return item.checklist.filter((item) => item.isCompleted).length;
        },
        view() {
            const workflow = this.workflows[this.workflowId];
            const permissions = ["createTask", "editTask", "draggingTask", "deleteTask"].map(permission => workflow.permissions[permission]);
            const perm = permissions.every(permission => permission.length === 0);
            if (perm || permissions[1].includes(this.user._id)) {
                this.addNewWindow({
                    type: "editor-task",
                    title: this.item.title,
                    payload: {
                        taskId: this.item._id,
                    },
                });
            } else {
                this.$notification.error({
                    message: this.$t("task.permEditTask"),
                    description: this.$t("task.permEditTaskDes"),
                });
            }
        },
        check(item, e) {
            this.setCheckedTasks({
                ...this.checkedItems,
                [item._id]: e.target.checked,
            });
        },
        edit() {
            const workflow = this.workflows[this.workflowId];
            const permissions = ["createTask", "editTask", "draggingTask", "deleteTask"].map(permission => workflow.permissions[permission]);
            const perm = permissions.every(permission => permission.length === 0);
            if (perm || permissions[1].includes(this.user._id)) {
                this.addNewWindow({
                    type: "edit-task",
                    payload: {
                        taskId: this.item._id,
                    },
                });
            } else {
                this.$notification.error({
                    message: this.$t("task.permEditTask"),
                    description: this.$t("task.permEditTaskDes"),
                });
            }
            // this.addNewWindow({
            //     type: "edit-task",
            //     payload: {
            //         taskId: this.item._id,
            //     },
            // });
            this.showPopover = false;
        },
        deleteTaskModalOpen(item) {
            this.$emit("delete-item", item);
            this.showPopover = false;
        },
        isShowingAction(action) {
            return (
                !action?.type ||
                (action.type === "goto_message" && this.relations["message"]) ||
                (action.type === "goto_chat" && this.relations["conversation"]) ||
                (action.type === "goto_visitor" && this.relations["visitor"])
            );
        },
        gotoMessage(item) {
            this.showPopover = false;
            // go to archive
        },
        gotoChat(item) {
            this.showPopover = false;
        },
        showVisitor(item) {
            this.addNewWindow({
                type: "show-visitor",
                payload: {
                    visitorId: this.relations["visitor"].visitor,
                },
            });
            this.showPopover = false;
        },
        runAction(fnName, item) {
            if (!this.checkPermission(PERMISSION_TYPES["task.delete"].tr.name) && fnName === "deleteTaskModalOpen") {
                this.$message.error(this.$t("noPermission"));
                return;
            }
            this[fnName](item);
        },
        handleInnerClick(e) {
            e.stopPropagation();
        },
    },
    mounted() {
        this.relations = this.item.relations.reduce((acc, relation) => {
            acc[relation.type] = relation;
            return acc;
        }, {});
    },
    watch: {
        checkedItems: {
            deep: true,
            handler() {
                this.selected = this.checkedItems[this.item._id];
            },
        },
    },
};
</script>
<style lang="scss">
.task-more-options {
    padding-top: 0 !important;
    .ant-popover-arrow {
        display: none !important;
    }
    .ant-popover-inner-content {
        box-shadow: none !important;
    }
    .ant-popover-inner-content {
        padding: 5px 0px;
    }
    .options-item {
        width: 200px;
        display: flex;
        align-items: center;
        padding: 5px 10px;
        gap: 10px;
        font-weight: 500;
        font-size: 15px;
        color: #55555a;
        cursor: pointer;
        &:hover {
            background-color: #f1f3f9;
        }
        .material-icons {
            color: #91a2b6;
        }
    }
    .bracket {
        width: 100%;
        height: 1px;
        margin: 3px 0px;
        background-color: #e6e9f2;
    }
}

.drag-pipeline-row:hover {
    border-radius: 4px !important;
    border: 2px solid var(--pipeline-color) !important;
}

#image-preview {
    cursor: pointer;
    width: auto;
    height: 100px;
    margin-bottom: -10px;
    border-radius: 2px;
    overflow: hidden;
    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
}
</style>
